// Vendor Companies — tenant listesi + detay modal + onboarding wizard
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { Button, EmptyState, Modal, FormField, Pager, ListPageHeader, FilterBar, ActionsMenu } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

const STATUS_COLORS = {
  active:        { bg: 'rgba(16,185,129,0.15)', clr: 'var(--success)' },
  trial:         { bg: 'rgba(59,130,246,0.15)',  clr: 'var(--info)' },
  trial_expired: { bg: 'rgba(245,158,11,0.15)',  clr: '#f59e0b' },
  past_due:      { bg: 'rgba(245,158,11,0.15)',  clr: '#f59e0b' },
  suspended:     { bg: 'rgba(239,68,68,0.15)',   clr: 'var(--danger)' },
  cancelled:     { bg: 'rgba(100,116,139,0.15)', clr: '#64748b' },
};
const STATUS_LABELS = {
  active: 'Aktif', trial: 'Trial', trial_expired: 'Trial Bitti',
  past_due: 'Vade Geçmiş', suspended: 'Askıda', cancelled: 'İptal',
};

const ALL_MODULES = [
  { key: 'core',         label: 'Çekirdek ERP' },
  { key: 'pergola',      label: 'Pergole / Persan' },
  { key: 'ecommerce',    label: 'e-Ticaret' },
  { key: 'service',      label: 'Servis / Bakım' },
  { key: 'manufacturing',label: 'Üretim' },
  { key: 'restaurant',   label: 'Restoran / Cafe' },
  { key: 'pos',          label: 'POS Hızlı Satış' },
];

export default function VendorCompaniesPage({ onNavigate } = {}) {
  const { startImpersonation } = useAuth();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '' });
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);

  const doImpersonate = async (companyId, companyName) => {
    if (!window.confirm(`"${companyName}" firmasına müşteri olarak giriş yapılacak. Devam edilsin mi?`)) return;
    setBusy('imp-' + companyId);
    try {
      const r = await api.post('/vendor/companies/' + companyId + '/impersonate');
      if (r.data.success) {
        startImpersonation(r.data.data.token);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Hata');
      setBusy(null);
    }
  };

  const doExtendTrial = async (companyId, days = 14) => {
    setBusy('trial-' + companyId);
    try {
      const reason = window.prompt('Uzatma sebebi (opsiyonel):', 'Manuel uzatma');
      const r = await api.post('/vendor/companies/' + companyId + '/extend-trial', { days, reason });
      if (r.data.success) {
        alert(`Trial ${days} gün uzatıldı`);
        load();
      }
    } catch (err) { alert(err.response?.data?.message || 'Hata'); }
    setBusy(null);
  };

  const doSuspend = async (companyId, companyName) => {
    const reason = window.prompt(`"${companyName}" askıya alma sebebi:`);
    if (!reason) return;
    setBusy('susp-' + companyId);
    try {
      await api.post('/vendor/companies/' + companyId + '/suspend', { reason });
      load();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); }
    setBusy(null);
  };

  const doReactivate = async (companyId) => {
    if (!window.confirm('Firma aktifleştirilecek. Devam edilsin mi?')) return;
    setBusy('rea-' + companyId);
    try {
      await api.post('/vendor/companies/' + companyId + '/reactivate');
      load();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); }
    setBusy(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendor/companies', { params: { search, ...filters, page, limit } });
      if (r.data.success) { setList(r.data.data || []); setTotal(r.data.total || 0); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, filters, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/vendor/plans').then(r => { if (r.data.success) setPlans(r.data.data || []); });
  }, []);

  const openDetail = async (id) => {
    // Yeni sekmeli detay sayfasına yönlendir (eski modal yerine)
    if (onNavigate) {
      onNavigate('vendor-customer-detail', id);
    } else {
      // Fallback (geriye uyum)
      try {
        const r = await api.get('/vendor/companies/' + id);
        if (r.data.success) setDetail(r.data.data);
      } catch (e) { alert('Yüklenemedi'); }
    }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Firmalar (Tenant)"
        unit="firma"
        count={total}
        searchValue={search}
        onSearch={setSearch}
        searchPlaceholder="Kod, ünvan, vergi no ile ara..."
        showFilterButton
        filtersActive={showFilterBar}
        onToggleFilters={() => setShowFilterBar(s => !s)}
        onRefresh={load}
        refreshing={loading}
        actions={<Button size="sm" onClick={() => setShowWizard(true)}>+ Yeni Firma</Button>}
      />

      <FilterBar
        active={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'status', type: 'select', label: 'Durum', options: [
            { value: '', label: 'Tümü' },
            ...Object.entries(STATUS_LABELS).map(([k,v]) => ({ value: k, label: v })),
          ]},
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="vendor-companies-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Firma bulunamadı"
          data={list}
          rowKey="id"
          onRowClick={c => openDetail(c.id)}
          columns={[
            { id: 'code', header: 'Kod', accessor: 'code', sortable: true, width: 90,
              cell: c => <span className="stock-code">{c.code}</span> },
            { id: 'name', header: 'Ünvan', accessor: 'name', sortable: true,
              cell: c => <span className="stock-name-cell">{c.name}</span> },
            { id: 'plan', header: 'Plan', accessor: 'plan_name', sortable: true, width: 130,
              cell: c => <span className="stock-secondary">{c.plan_name || '-'}</span> },
            { id: 'billing', header: 'Periyot', accessor: 'billing_period', sortable: true, width: 90,
              cell: c => <span className="stock-secondary">{c.billing_period === 'yearly' ? 'Yıllık' : c.billing_period === 'monthly' ? 'Aylık' : '-'}</span> },
            { id: 'status', header: 'Durum', accessor: 'subscription_status', sortable: true, width: 130,
              cell: c => {
                const col = STATUS_COLORS[c.subscription_status] || {};
                return <span style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 'var(--r-full)', fontWeight: 700, background: col.bg, color: col.clr }}>
                  {STATUS_LABELS[c.subscription_status] || c.subscription_status}
                </span>;
              } },
            { id: 'ends_at', header: 'Bitiş', accessor: c => c.subscription_ends_at || c.trial_ends_at, sortable: true, width: 130,
              cell: c => {
                const v = c.subscription_ends_at || c.trial_ends_at;
                if (!v) return <span className="stock-secondary">-</span>;
                const days = Math.ceil((new Date(v) - Date.now()) / 86400000);
                return <span style={{ color: days < 0 ? 'var(--danger)' : days <= 3 ? '#f59e0b' : 'var(--text-primary)', fontWeight: days <= 7 ? 600 : 400, fontSize: 13 }}>
                  {new Date(v).toLocaleDateString('tr-TR')}
                  {days >= 0 && days <= 14 && <span style={{ fontSize: 11, marginLeft: 4 }}>({days}g)</span>}
                </span>;
              } },
            { id: 'users', header: 'Kullanıcı', accessor: c => `${c.user_count || 0} / ${c.user_limit || '∞'}`, width: 110,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: c => {
                const used = c.user_count || 0;
                const lim = c.user_limit;
                const near = lim && used >= lim * 0.8;
                return <span style={{ color: near ? '#f59e0b' : 'var(--text-primary)', fontWeight: near ? 700 : 400 }}>
                  {used} / {lim || '∞'}
                </span>;
              } },
            { id: 'price', header: 'Aylık Tutar', accessor: c => parseFloat(c.monthly_price) || 0, sortable: true, width: 110,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: c => <span className="stock-price-cell">{c.monthly_price ? `₺${fmt(c.monthly_price)}` : '-'}</span> },
            { id: 'health', header: 'Sağlık', width: 80,
              cell: c => {
                // Basit health hesabı (frontend tarafı kestirme; backend gerçek view'dan döner)
                const v = c.subscription_ends_at || c.trial_ends_at;
                const days = v ? Math.ceil((new Date(v) - Date.now()) / 86400000) : 999;
                let dot = '🟢', label = 'Sağlıklı';
                if (['suspended','cancelled'].includes(c.subscription_status)) { dot = '🔴'; label = 'Kritik'; }
                else if (['past_due','trial_expired'].includes(c.subscription_status)) { dot = '🔴'; label = 'Riskli'; }
                else if (days < 0) { dot = '🟠'; label = 'Vade geçmiş'; }
                else if (days <= 3) { dot = '🟡'; label = `${days}g kaldı`; }
                else if (days <= 14) { dot = '🟡'; label = `${days}g`; }
                return <span title={label} style={{ fontSize: '0.9rem' }}>{dot}</span>;
              } },
            { id: 'actions', header: '', width: 50, cellStyle: { textAlign: 'right' },
              cell: c => (
                <div onClick={e => e.stopPropagation()}>
                  <ActionsMenu buttonLabel="⋯" items={[
                    { label: 'Müşteri olarak gir', icon: '🎭', onClick: () => doImpersonate(c.id, c.name), disabled: busy === 'imp-' + c.id },
                    { label: 'Detay göster',       icon: '📋', onClick: () => openDetail(c.id) },
                    { separator: true },
                    { label: 'Trial +14 gün', icon: '⏰', onClick: () => doExtendTrial(c.id, 14),
                      disabled: !['trial','trial_expired','suspended'].includes(c.subscription_status) },
                    { label: 'Trial +30 gün', icon: '⏰', onClick: () => doExtendTrial(c.id, 30),
                      disabled: !['trial','trial_expired','suspended'].includes(c.subscription_status) },
                    { separator: true },
                    ...(c.subscription_status !== 'suspended'
                      ? [{ label: 'Askıya al', icon: '⛔', onClick: () => doSuspend(c.id, c.name), variant: 'danger' }]
                      : [{ label: 'Re-aktive et', icon: '✅', onClick: () => doReactivate(c.id) }]),
                  ]} />
                </div>
              ) },
          ]}
        />
      </div>

      <div style={{ padding: '8px 12px' }}>
        <Pager page={page} total={total} limit={limit} onPage={setPage} />
      </div>

      {/* Yeni firma wizard */}
      {showWizard && <NewCompanyWizard plans={plans} onClose={() => setShowWizard(false)} onCreated={() => { setShowWizard(false); load(); }} />}

      {/* Detay modal */}
      {detail && <CompanyDetailModal data={detail} plans={plans} onClose={() => setDetail(null)} onUpdated={() => { openDetail(detail.company.id); load(); }} />}
    </div>
  );
}

// ─── Yeni Firma Wizard ────────────────────────────────────────────
function NewCompanyWizard({ plans, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '', tax_number: '', tax_office: '', address: '', city: '', district: '', country: 'Türkiye',
    phone: '', email: '',
    plan_id: plans.find(p => p.code === 'trial')?.id || plans[0]?.id || '',
    billing_period: 'monthly',
    custom_user_limit: null,
    admin_full_name: '', admin_email: '', admin_password: '',
    module_keys: ['core'],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Türkiye şehirleri ve ilçeleri otomatik yüklenir
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  useEffect(() => {
    // Türkiye ülkesini bul, şehirlerini çek
    api.get('/locations/countries')
      .then(r => {
        const tr = (r.data?.data || []).find(c => /türkiye|turkiye|turkey/i.test(c.name));
        if (tr) {
          api.get('/locations/cities', { params: { country_id: tr.id } })
            .then(rc => setCities(rc.data?.data || []))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);
  // Şehir değişince ilçeleri çek
  useEffect(() => {
    if (!form.city) { setDistricts([]); return; }
    const sel = cities.find(c => c.name === form.city);
    if (!sel) { setDistricts([]); return; }
    api.get('/locations/districts', { params: { city_id: sel.id } })
      .then(r => setDistricts(r.data?.data || []))
      .catch(() => setDistricts([]));
  }, [form.city, cities]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleMod = (k) => setForm(p => ({ ...p, module_keys: p.module_keys.includes(k) ? p.module_keys.filter(x => x !== k) : [...p.module_keys, k] }));

  const next = () => {
    setError('');
    if (step === 1 && !form.name.trim()) { setError('Firma ünvanı zorunlu'); return; }
    if (step === 4 && (!form.admin_full_name || !form.admin_email || !form.admin_password)) {
      setError('Admin bilgileri zorunlu'); return;
    }
    setStep(s => s + 1);
  };
  const prev = () => setStep(s => Math.max(1, s - 1));

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const r = await api.post('/vendor/companies', form);
      if (r.data.success) onCreated();
    } catch (e) { setError(e.response?.data?.message || 'Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const selectedPlan = plans.find(p => p.id === form.plan_id);

  return (
    <Modal
      title={`Yeni Firma · Adım ${step} / 5`}
      size="lg"
      onClose={onClose}
      primaryAction={step < 5 ? { label: 'İleri →', onClick: next } : { label: '✓ Firmayı Oluştur', onClick: submit, loading: saving, variant: 'primary' }}
      cancelAction={step > 1 ? { label: '← Geri', onClick: prev } : { label: 'Vazgeç', onClick: onClose }}
    >
      {error && <div style={{ padding: 10, marginBottom: 12, background: 'rgba(220,38,38,0.08)', color: '#991b1b', borderRadius: 4 }}>{error}</div>}

      <Stepper step={step} steps={['Firma', 'Plan', 'Modüller', 'Admin', 'Onay']} />

      {step === 1 && (
        <div className="form-grid" style={{ marginTop: 16 }}>
          <FormField label="Ünvan" required><input value={form.name} onChange={e => f('name', e.target.value)} autoFocus /></FormField>
          <FormField label="Vergi No"><input value={form.tax_number} onChange={e => f('tax_number', e.target.value)} /></FormField>
          <FormField label="Vergi Dairesi"><input value={form.tax_office} onChange={e => f('tax_office', e.target.value)} /></FormField>
          <FormField label="Telefon"><input value={form.phone} onChange={e => f('phone', e.target.value)} /></FormField>
          <FormField label="E-posta"><input value={form.email} onChange={e => f('email', e.target.value)} /></FormField>
          <FormField label="Ülke">
            <input value={form.country || 'Türkiye'} onChange={e => f('country', e.target.value)} readOnly style={{ background: 'var(--bg-secondary)' }} />
          </FormField>
          <FormField label="Şehir">
            <select value={form.city || ''} onChange={e => { f('city', e.target.value); f('district', ''); }}>
              <option value="">— Şehir seçin —</option>
              {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </FormField>
          <FormField label="İlçe">
            {districts.length > 0 ? (
              <select value={form.district || ''} onChange={e => f('district', e.target.value)}>
                <option value="">— İlçe seçin —</option>
                {districts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            ) : (
              <input value={form.district || ''} onChange={e => f('district', e.target.value)} placeholder={form.city ? 'İlçe (manuel)' : 'Önce şehir seçin'} />
            )}
          </FormField>
          <div className="form-row full"><FormField label="Adres" fullWidth><textarea rows={2} value={form.address} onChange={e => f('address', e.target.value)} /></FormField></div>
        </div>
      )}

      {step === 2 && (
        <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
          {plans.filter(p => p.is_active !== false).map(p => (
            <div key={p.id} onClick={() => f('plan_id', p.id)} style={{
              padding: 12, border: form.plan_id === p.id ? '2px solid var(--accent-primary, #2563eb)' : '1px solid var(--border-color)',
              borderRadius: 6, cursor: 'pointer',
              background: form.plan_id === p.id ? 'rgba(37,99,235,0.05)' : 'transparent',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="radio" checked={form.plan_id === p.id} readOnly />
                <strong>{p.name}</strong>
                {p.is_trial && <span style={{ fontSize: 11, padding: '2px 8px', background: 'rgba(59,130,246,0.15)', color: 'var(--info)', borderRadius: 4, fontWeight: 700 }}>{p.trial_days} gün ücretsiz</span>}
                <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-secondary)' }}>
                  {p.is_trial ? 'Ücretsiz' : `₺${fmt(p.monthly_price)}/ay · ₺${fmt(p.yearly_price)}/yıl`}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                Kullanıcı: {p.user_limit || '∞'} · Stok: {p.stock_limit ? fmt(p.stock_limit) : '∞'} · Cari: {p.account_limit ? fmt(p.account_limit) : '∞'} · Storage: {p.storage_mb_limit ? `${p.storage_mb_limit} MB` : '∞'}
              </div>
            </div>
          ))}
          {!selectedPlan?.is_trial && (
            <FormField label="Faturalama Periyodu">
              <select value={form.billing_period} onChange={e => f('billing_period', e.target.value)}>
                <option value="monthly">Aylık</option>
                <option value="yearly">Yıllık (genelde indirimli)</option>
              </select>
            </FormField>
          )}
        </div>
      )}

      {step === 3 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Aktif olacak modülleri seçin (sonradan değiştirilebilir):
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 8 }}>
            {ALL_MODULES.map(m => (
              <label key={m.key} style={{
                padding: 10, border: '1px solid var(--border-color)', borderRadius: 6, cursor: 'pointer',
                background: form.module_keys.includes(m.key) ? 'rgba(37,99,235,0.08)' : 'transparent',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <input type="checkbox" checked={form.module_keys.includes(m.key)} onChange={() => toggleMod(m.key)} />
                <span>{m.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="form-grid" style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', gridColumn: '1/-1', marginBottom: 8 }}>
            Bu firma için ilk admin kullanıcısı (otomatik şifre gönderilmez — bu şifreyi müşteriye iletmeniz gerekir):
          </div>
          <FormField label="Ad Soyad" required><input value={form.admin_full_name} onChange={e => f('admin_full_name', e.target.value)} /></FormField>
          <FormField label="E-posta" required><input type="email" value={form.admin_email} onChange={e => f('admin_email', e.target.value)} /></FormField>
          <FormField label="Geçici Şifre" required><input type="text" value={form.admin_password} onChange={e => f('admin_password', e.target.value)} /></FormField>
        </div>
      )}

      {step === 5 && (
        <div style={{ marginTop: 16, padding: 14, background: 'var(--bg-secondary, #f8fafc)', borderRadius: 6 }}>
          <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>Onay:</div>
          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            <div><strong>Firma:</strong> {form.name}</div>
            <div><strong>Plan:</strong> {selectedPlan?.name} {!selectedPlan?.is_trial && `(${form.billing_period === 'yearly' ? 'Yıllık' : 'Aylık'})`}</div>
            <div><strong>Modüller:</strong> {form.module_keys.map(k => ALL_MODULES.find(m => m.key === k)?.label).join(', ')}</div>
            <div><strong>Admin:</strong> {form.admin_full_name} ({form.admin_email})</div>
            <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 8 }}>"Firmayı Oluştur" tuşuna bastığınızda firma + admin kullanıcısı oluşturulur.</div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Stepper({ step, steps }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
      {steps.map((s, i) => {
        const n = i + 1;
        return (
          <div key={s} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: 4, borderRadius: 2,
              background: n <= step ? 'var(--accent-primary, #2563eb)' : 'var(--border-color, #e5e5e5)',
              marginBottom: 4,
            }} />
            <div style={{ fontSize: 11, color: n === step ? 'var(--accent-primary)' : 'var(--text-tertiary)', fontWeight: n === step ? 700 : 400 }}>
              {n}. {s}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Detay Modal ──────────────────────────────────────────────────
function CompanyDetailModal({ data, plans, onClose, onUpdated }) {
  const c = data.company;
  const [tab, setTab] = useState('subscription');
  const [edit, setEdit] = useState({
    current_plan_id: c.current_plan_id, billing_period: c.billing_period || 'monthly',
    user_limit: c.user_limit, stock_limit: c.stock_limit, account_limit: c.account_limit,
    monthly_invoice_limit: c.monthly_invoice_limit, storage_mb_limit: c.storage_mb_limit,
    monthly_price: c.monthly_price, yearly_price: c.yearly_price,
    auto_renew: !!c.auto_renew, vendor_notes: c.vendor_notes || '',
    subscription_status: c.subscription_status, suspended_reason: c.suspended_reason || '',
    trial_ends_at: c.trial_ends_at?.substring(0,10) || '',
    subscription_ends_at: c.subscription_ends_at?.substring(0,10) || '',
  });
  const [saving, setSaving] = useState(false);

  const saveSubscription = async () => {
    setSaving(true);
    try {
      await api.put('/vendor/companies/' + c.id, edit);
      onUpdated();
    } catch (e) { alert(e.response?.data?.message || 'Hata'); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      title={`${c.code} · ${c.name}`}
      subtitle={<span style={{ color: 'var(--text-secondary)' }}>Plan: {c.plan_name || '-'} · Durum: {STATUS_LABELS[c.subscription_status] || c.subscription_status}</span>}
      size="xl"
      onClose={onClose}
      primaryAction={tab === 'subscription' ? { label: 'Aboneliği Kaydet', onClick: saveSubscription, loading: saving } : null}
      cancelAction={{ label: 'Kapat', onClick: onClose }}
    >
      <div className="detail-tabs" style={{ marginBottom: 12 }}>
        {['subscription','modules','users','limits','notes'].map(k => (
          <button key={k} className={'tab-btn' + (tab === k ? ' active' : '')} onClick={() => setTab(k)}>
            <span>{({ subscription:'Abonelik', modules:'Modüller', users:'Kullanıcılar', limits:'Limit Kullanımı', notes:'Notlar' })[k]}</span>
          </button>
        ))}
      </div>

      {tab === 'subscription' && (
        <div className="form-grid">
          <FormField label="Plan">
            <select value={edit.current_plan_id || ''} onChange={e => setEdit({ ...edit, current_plan_id: e.target.value || null })}>
              <option value="">— Plan Seçilmemiş —</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </FormField>
          <FormField label="Durum">
            <select value={edit.subscription_status} onChange={e => setEdit({ ...edit, subscription_status: e.target.value })}>
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormField>
          <FormField label="Faturalama Periyodu">
            <select value={edit.billing_period} onChange={e => setEdit({ ...edit, billing_period: e.target.value })}>
              <option value="monthly">Aylık</option><option value="yearly">Yıllık</option>
            </select>
          </FormField>
          <FormField label="Aylık Fiyat (₺)"><input type="number" value={edit.monthly_price || 0} onChange={e => setEdit({ ...edit, monthly_price: parseFloat(e.target.value) || 0 })} /></FormField>
          <FormField label="Yıllık Fiyat (₺)"><input type="number" value={edit.yearly_price || 0} onChange={e => setEdit({ ...edit, yearly_price: parseFloat(e.target.value) || 0 })} /></FormField>
          <FormField label="Otomatik Yenileme">
            <select value={edit.auto_renew ? '1' : '0'} onChange={e => setEdit({ ...edit, auto_renew: e.target.value === '1' })}>
              <option value="1">Açık</option><option value="0">Kapalı</option>
            </select>
          </FormField>
          <FormField label="Trial Bitiş"><input type="date" value={edit.trial_ends_at} onChange={e => setEdit({ ...edit, trial_ends_at: e.target.value })} /></FormField>
          <FormField label="Abonelik Bitiş"><input type="date" value={edit.subscription_ends_at} onChange={e => setEdit({ ...edit, subscription_ends_at: e.target.value })} /></FormField>
          <FormField label="Kullanıcı Sınırı (boş=sınırsız)"><input type="number" value={edit.user_limit ?? ''} onChange={e => setEdit({ ...edit, user_limit: e.target.value === '' ? null : parseInt(e.target.value) })} /></FormField>
          <FormField label="Stok Sınırı"><input type="number" value={edit.stock_limit ?? ''} onChange={e => setEdit({ ...edit, stock_limit: e.target.value === '' ? null : parseInt(e.target.value) })} /></FormField>
          <FormField label="Cari Sınırı"><input type="number" value={edit.account_limit ?? ''} onChange={e => setEdit({ ...edit, account_limit: e.target.value === '' ? null : parseInt(e.target.value) })} /></FormField>
          <FormField label="Aylık Fatura Sınırı"><input type="number" value={edit.monthly_invoice_limit ?? ''} onChange={e => setEdit({ ...edit, monthly_invoice_limit: e.target.value === '' ? null : parseInt(e.target.value) })} /></FormField>
          <FormField label="Storage Sınırı (MB)"><input type="number" value={edit.storage_mb_limit ?? ''} onChange={e => setEdit({ ...edit, storage_mb_limit: e.target.value === '' ? null : parseInt(e.target.value) })} /></FormField>
          {edit.subscription_status === 'suspended' && (
            <FormField label="Askı Sebebi" fullWidth>
              <input value={edit.suspended_reason} onChange={e => setEdit({ ...edit, suspended_reason: e.target.value })} placeholder="Müşteriye gösterilecek mesaj..." />
            </FormField>
          )}
        </div>
      )}

      {tab === 'modules' && <ModulesTab companyId={c.id} mods={data.modules} onChanged={onUpdated} />}

      {tab === 'users' && (
        <div>
          {data.users.length === 0 ? <EmptyState title="Kullanıcı yok" compact /> : (
            <table className="stocks-table">
              <thead><tr><th>Ad</th><th>E-posta</th><th>Rol</th><th>Son Giriş</th><th>Aktif</th></tr></thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u.id}>
                    <td><strong>{u.full_name}</strong></td>
                    <td><span className="stock-secondary">{u.email}</span></td>
                    <td><span className="stock-secondary">{u.role}</span></td>
                    <td><span className="stock-secondary">{u.last_login ? new Date(u.last_login).toLocaleString('tr-TR') : '-'}</span></td>
                    <td>{u.is_active ? '✓' : '✗'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'limits' && <LimitsTab limits={data.limits} />}

      {tab === 'notes' && (
        <FormField label="Vendor Notları (sadece bizim görüyoruz)" fullWidth>
          <textarea rows={10} value={edit.vendor_notes} onChange={e => setEdit({ ...edit, vendor_notes: e.target.value })} placeholder="Bu firma hakkında dahili notlar..." style={{ width: '100%' }} />
        </FormField>
      )}
    </Modal>
  );
}

function ModulesTab({ companyId, mods, onChanged }) {
  const [saving, setSaving] = useState(null);
  const isEnabled = (k) => mods.some(m => m.module_key === k && m.is_enabled);

  const toggle = async (k) => {
    setSaving(k);
    try {
      await api.post('/vendor/companies/' + companyId + '/modules', { module_key: k, is_enabled: !isEnabled(k) });
      onChanged();
    } catch (e) { alert('Hata'); }
    finally { setSaving(null); }
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {ALL_MODULES.map(m => (
        <div key={m.key} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: 10, border: '1px solid var(--border-color)', borderRadius: 6,
        }}>
          <input type="checkbox" checked={isEnabled(m.key)} onChange={() => toggle(m.key)} disabled={saving === m.key}
                 style={{ width: 18, height: 18 }} />
          <span style={{ flex: 1, fontWeight: 500 }}>{m.label}</span>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{isEnabled(m.key) ? 'Aktif' : 'Pasif'}</span>
        </div>
      ))}
    </div>
  );
}

function LimitsTab({ limits }) {
  const items = [
    { key: 'user', label: 'Kullanıcı' },
    { key: 'stock', label: 'Stok' },
    { key: 'account', label: 'Cari' },
    { key: 'monthly_invoice', label: 'Aylık Fatura/İrsaliye/Sipariş' },
    { key: 'storage', label: 'Depolama (MB)' },
  ];
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {items.map(it => {
        const l = limits[it.key];
        if (!l) return null;
        const pct = l.percentage || 0;
        const color = l.unlimited ? 'var(--success)' : pct >= 100 ? 'var(--danger)' : pct >= 80 ? '#f59e0b' : 'var(--success)';
        return (
          <div key={it.key} style={{ padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <strong style={{ fontSize: 13 }}>{it.label}</strong>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color }}>
                {l.unlimited ? 'Sınırsız' : `${fmt(l.used)} / ${fmt(l.limit)} (${pct}%)`}
              </span>
            </div>
            {!l.unlimited && (
              <div style={{ height: 6, background: 'var(--bg-secondary)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
